<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-glass">
        <i class="far fa-layer-group"></i> Glass Stock Value
      </Tab>
      <Tab tabid="tab-general">
        <i class="far fa-box-up"></i> General Stock Value
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-glass">
        <DataTable
          ref="glassStockValueTable"
          :columns="glassStockColumnList"
          :url="glassStockValueURL"
          :useReportingServer="true"
          :displayTools="true"
          :defaultColumnOrderIndex="8"
          defaultColumnOrderDir="DESC"
          :displayExportTools="true"
          exportTitle="Glass Stock Value On Hand"
        >
          <template v-slot:title>
            <ContentTitle>Glass Stock Value List as at {{appliedGlassValueFilter.date | formatDate}}</ContentTitle>
            <ContentSubTitle>Data updated every 30 minutes</ContentSubTitle>
          </template>
          <template v-slot:tools>
            <ReportFilterPopover
              headerTitle="Glass Stock Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applyGlassValueFilter"
              @reset="resetGlassValueFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Date Filter">
                  <ReportDateSelect
                    :date="editingGlassValueFilter.date"
                    @updateDate="editingGlassValueFilter.date = $event"
                  />
                </ReportFilterGroup>
                <ReportFilterGroup label="Options">
                  <div class="row">
                    <div class="col-6">
                      <FormRow label="Include Sheets / Blocks">
                        <FormSwitch booleanMode v-model="editingGlassValueFilter.includeSheets" />
                      </FormRow>
                    </div>
                    <div class="col-6">
                      <FormRow label="Include Offcuts">
                        <FormSwitch booleanMode v-model="editingGlassValueFilter.includeOffcuts" />
                      </FormRow>
                    </div>
                  </div>
                </ReportFilterGroup>
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedGlassValueFilter.date | formatDate}}
                </div>                    
              </template>
            </ReportFilterPopover>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                <LinkStock :name="row.data.stockCode" :id="row.data.stockId" />
              </td>
              <td>{{row.data.glassSizeHeight}} x {{row.data.glassSizeWidth}}</td>
              <td v-if="displaySheets">{{row.data.totalLooseSheetsOnHand | formatInteger(false, true)}}</td>
              <td v-if="displaySheets">{{row.data.totalBlocksOnHand | formatInteger(false, true)}}</td>
              <td v-if="displaySheets">{{row.data.totalBlocksSheetsOnHand | formatInteger(false, true)}}</td>
              <td v-if="displayOffcuts">{{row.data.totalOffcutSheetsOnHand | formatInteger(false, true)}}</td>
              <td v-if="displaySheets">{{row.data.totalSheetsOnHand | formatInteger(false, true)}}</td>
              <td v-if="displaySheets">{{row.data.totalLooseUnitsOnHand | formatDecimal(5, true)}} m&sup2;</td>
              <td v-if="displaySheets">{{row.data.totalBlockUnitsOnHand | formatDecimal(5, true)}} m&sup2;</td>
              <td v-if="displayOffcuts">{{row.data.totalOffcutSheetUnitsOnHand | formatDecimal(5, true)}} m&sup2;</td>

              <td v-if="displaySheets && displayOffcuts">{{row.data.totalUnitsOnHand | formatDecimal(5, true)}} m&sup2;</td>
              <td v-if="displaySheets && !displayOffcuts">{{row.data.totalUnitsOnHandMinusOffcut | formatDecimal(5, true)}} m&sup2;</td>
              <td v-if="!displaySheets && displayOffcuts">{{row.data.totalOffcutSheetUnitsOnHand | formatDecimal(5, true)}} m&sup2;</td>

              <td>{{row.data.averageUnitValue | formatCurrency}}</td>

              <td v-if="displaySheets && displayOffcuts">{{row.data.totalStockValue | formatCurrency}}</td>
              <td v-if="displaySheets && !displayOffcuts">{{row.data.totalStockValueMinusOffcut | formatCurrency}}</td>
              <td v-if="!displaySheets && displayOffcuts">{{row.data.totalOffcutValue | formatCurrency}}</td>
            </tr>
          </template>
          <template v-slot:summary="summary">
            <tr key="summary">
              <td>Totals</td>
              <td></td>
              <td v-if="displaySheets">{{formatInteger(summary.data.totalLooseSheetsOnHand, false, true)}}</td>
              <td v-if="displaySheets">{{formatInteger(summary.data.totalBlocksOnHand, false, true)}}</td>
              <td v-if="displaySheets">{{formatInteger(summary.data.totalBlocksSheetsOnHand, false, true)}}</td>
              
              <td v-if="displayOffcuts">{{formatInteger(summary.data.totalOffcutSheetsOnHand, false, true)}}</td>

              <td v-if="displaySheets">{{formatInteger(summary.data.totalSheetsOnHand, false, true)}}</td>
              <td v-if="displaySheets">{{formatDecimal(summary.data.totalLooseUnitsOnHand, 5, true)}}m&sup2;</td>
              <td v-if="displaySheets">{{formatDecimal(summary.data.totalBlockUnitsOnHand, 5, true)}}m&sup2;</td>
              
              <td v-if="displayOffcuts">{{formatDecimal(summary.data.totalOffcutSheetUnitsOnHand, 5, true)}}m&sup2;</td>

              <td v-if="displaySheets && displayOffcuts">{{formatDecimal(summary.data.totalUnitsOnHand, 5, true)}}m&sup2;</td>
              <td v-if="displaySheets && !displayOffcuts">{{formatDecimal(summary.data.totalUnitsOnHandMinusOffcut, 5, true)}}m&sup2;</td>
              <td v-if="!displaySheets && displayOffcuts">{{formatDecimal(summary.data.totalOffcutSheetUnitsOnHand, 5, true)}}m&sup2;</td>

              <td></td>

              <td v-if="displaySheets && displayOffcuts">{{formatCurrency(summary.data.totalStockValue)}}</td>
              <td v-if="displaySheets && !displayOffcuts">{{formatCurrency(summary.data.totalStockValueMinusOffcut)}}</td>
              <td v-if="!displaySheets && displayOffcuts">{{formatCurrency(summary.data.totalOffcutValue)}}</td>
            </tr>
          </template>
        </DataTable>

      </TabPage>
      <TabPage tabid="tab-general">
        <DataTable
          ref="generalStockValueTable"
          :columns="generalStockColumnList"
          :url="generalStockValueURL"
          :useReportingServer="true"
          :displayTools="true"
          :defaultColumnOrderIndex="3"
          defaultColumnOrderDir="DESC"
          :displayExportTools="true"
          exportTitle="General Stock Value On Hand"
        >
          <template v-slot:title>
            <ContentTitle>General Stock Value List as at {{appliedGeneralValueFilter.date | formatDate}}</ContentTitle>
            <ContentSubTitle>Data updated every 30 minutes</ContentSubTitle>
          </template>
          <template v-slot:tools>
            <ReportFilterPopover
              headerTitle="General Stock Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applyGeneralValueFilter"
              @reset="resetGeneralValueFilter">
              <template v-slot:popover>
                <ReportDateSelect
                  :date="editingGeneralValueFilter.date"
                  @updateDate="editingGeneralValueFilter.date = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedGeneralValueFilter.date | formatDate}}
                </div>                    
              </template>
            </ReportFilterPopover>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                <LinkStock :name="row.data.code" :id="row.data.id" />
              </td>
              <template v-if="row.data.stockValueHistoryLogs[0]">
                <td>{{row.data.stockValueHistoryLogs[0].unitsOnHand | formatDecimal(5, true)}} m&sup2;</td>
                <td>{{row.data.stockValueHistoryLogs[0].unitValue | formatCurrency}}</td>
                <td>{{row.data.totalStockValue | formatCurrency}}</td>
              </template>
              <template v-else>
                <td>-</td>
                <td>-</td>
                <td>-</td>
              </template>
            </tr>
          </template>
          <template v-slot:summary="summary">
            <tr key="summary">
              <td>Totals</td>
              <td>{{summary.data.totalUnitsOnHand | formatDecimal(5, true)}} m&sup2;</td>
              <td></td>
              <td>{{summary.data.totalStockValue | formatCurrency}}</td>
            </tr>
          </template>
        </DataTable>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import ReportDateSelect from '@/components/reporting/reportDateSelect'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

export default {
  name: 'StockValueOnHand',
  components: {
    ReportFilterPopover,
    ReportFilterGroup,
    ReportDateSelect,
    ReportDateRangeSelect
  },
  data() {
    return {
     
      defaultGlassValueFilter: {
        date: moment().local().endOf('day').utc().toDate().toISOString(),
        includeSheets: true,
        includeOffcuts: true
      },
      editingGlassValueFilter: {},
      appliedGlassValueFilter: {},

      defaultGeneralValueFilter: {
        date: moment().local().endOf('day').utc().toDate().toISOString()
      },
      editingGeneralValueFilter: {},
      appliedGeneralValueFilter: {},

      generalStockColumnList: [
        { title: "Stock", data: "code" },
        { title: "Units on Hand", data: "stockValueHistoryLogs.unitsOnHand", totalField: 'totalUnitsOnHand' },
        { title: "Unit Value", data: "stockValueHistoryLogs.unitValue", type: 'currency' },
        { title: "Stock Value on Hand", data: "totalStockValue", type: 'currency', totalField: 'totalStockValue' }
      ]
    }
  },
  computed: {
    glassStockValueURL() {
      var params = {
        date: this.appliedGlassValueFilter.date
      }
      return `intelligence/inventory/stockvalueonhand/glassstock?` + $.param(params)
    },
    generalStockValueURL() {
      var params = {
        date: this.appliedGeneralValueFilter.date
      }
      return `intelligence/inventory/stockvalueonhand/generalstock?` + $.param(params)
    },
    displaySheets() {
      return this.appliedGlassValueFilter.includeSheets
    },
    displayOffcuts() {
      return this.appliedGlassValueFilter.includeOffcuts
    },
    glassStockColumnList() {
      return _.compact([
        { title: "Stock", data: "stockCode" },
        { title: "Size (HxW)", data: "size" },
        this.displaySheets ? { title: "Loose Sheets", data: "totalLooseSheetsOnHand", totalField: 'totalLooseSheetsOnHand' } : null,
        this.displaySheets ? { title: "Blocks", data: "totalBlocksOnHand", totalField: 'totalBlocksOnHand' } : null,
        this.displaySheets ? { title: "Block Sheets", data: "totalBlocksSheetsOnHand", totalField: 'totalBlocksSheetsOnHand' } : null,
        this.displayOffcuts ? { title: "Offcuts", data: "totalOffcutSheetsOnHand", totalField: 'totalOffcutSheetsOnHand' } : null,
        this.displaySheets ? { title: "Total Sheets", data: "totalSheetsOnHand", totalField: 'totalSheetsOnHand' } : null,
        this.displaySheets ? { title: "Loose Units", data: "totalLooseUnitsOnHand", totalField: 'totalLooseUnitsOnHand' } : null,
        this.displaySheets ? { title: "Block Units", data: "totalBlockUnitsOnHand", totalField: 'totalBlockUnitsOnHand' } : null,
        this.displayOffcuts ? { title: "Offcut Units", data: "totalOffcutSheetUnitsOnHand", totalField: 'totalOffcutSheetUnitsOnHand' } : null,
        (this.displaySheets && this.displayOffcuts) ? { title: "Total Units on Hand", data: "totalUnitsOnHand", totalField: 'totalUnitsOnHand' } : null,
        (this.displaySheets && !this.displayOffcuts) ? { title: "Total Units on Hand", data: "totalUnitsOnHandMinusOffcut", totalField: 'totalUnitsOnHandMinusOffcut' } : null,
        (!this.displaySheets && this.displayOffcuts) ? { title: "Total Units on Hand", data: "totalOffcutSheetUnitsOnHand", totalField: 'totalOffcutSheetUnitsOnHand' } : null,
        
        { title: "Unit Value", data: "averageUnitValue", type: 'currency' },
        (this.displaySheets && this.displayOffcuts) ? { title: "Stock Value on Hand", data: "totalStockValue", type: 'currency', totalField: 'totalStockValue' } : null,
        (this.displaySheets && !this.displayOffcuts) ? { title: "Stock Value on Hand", data: "totalStockValueMinusOffcut", type: 'currency', totalField: 'totalStockValueMinusOffcut' } : null,
        (!this.displaySheets && this.displayOffcuts) ? { title: "Stock Value on Hand", data: "totalOffcutValue", type: 'currency', totalField: 'totalOffcutValue' } : null
      ])
    }
  },
  mounted() {
    this.editingGlassValueFilter = _.clone(this.defaultGlassValueFilter)
    this.appliedGlassValueFilter = _.clone(this.defaultGlassValueFilter)
    this.editingGeneralValueFilter = _.clone(this.defaultGeneralValueFilter)
    this.appliedGeneralValueFilter = _.clone(this.defaultGeneralValueFilter)
  },
  methods: {
    reloadGlassValueTable() {
      this.$refs.glassStockValueTable.reload()
    },
    reloadGeneralValueTable() {
      this.$refs.generalStockValueTable.reload()
    },
    applyGlassValueFilter() {
      this.appliedGlassValueFilter = _.clone(this.editingGlassValueFilter)

      this.reloadGlassValueTable()
    },
    resetGlassValueFilter() {
      this.editingGlassValueFilter = _.clone(this.defaultGlassValueFilter)
      this.appliedGlassValueFilter = _.clone(this.defaultGlassValueFilter)

      this.reloadGlassValueTable()
    },
    applyGeneralValueFilter() {
      this.appliedGeneralValueFilter = _.clone(this.editingGeneralValueFilter)

      this.reloadGeneralValueTable()
    },
    resetGeneralValueFilter() {
      this.editingGeneralValueFilter = _.clone(this.defaultGeneralValueFilter)
      this.appliedGeneralValueFilter = _.clone(this.defaultGeneralValueFilter)

      this.reloadGeneralValueTable()
    }
  }
}
</script>
